// src/components/MalwareAnalysisView.js
import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import { Bug, Search, Plus, FileCode, Shield, AlertTriangle, Hash, Calendar } from 'lucide-react';

const MalwareAnalysisView = ({ isSuperAdmin, canEditIncidents }) => {
  const { getToken } = useAuth();
  const { activeCompany } = useTenant();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMalware, setSelectedMalware] = useState(null);

  // Placeholder data
  const placeholderMalware = [
    {
      id: 1,
      name: 'Emotet',
      family: 'Banking Trojan',
      type: 'Trojan',
      threat_level: 'Critical',
      first_seen: '2024-01-15',
      last_seen: '2024-01-25',
      file_hash: 'a1b2c3d4e5f6...',
      file_size: '245 KB',
      file_type: 'PE32',
      affected_systems: ['Windows 10', 'Windows 11'],
      c2_servers: ['192.168.1.1', '10.0.0.1'],
      capabilities: ['Credential Theft', 'Lateral Movement', 'Persistence']
    },
    {
      id: 2,
      name: 'TrickBot',
      family: 'Banking Trojan',
      type: 'Trojan',
      threat_level: 'High',
      first_seen: '2024-01-10',
      last_seen: '2024-01-26',
      file_hash: 'f9e8d7c6b5a4...',
      file_size: '512 KB',
      file_type: 'PE32',
      affected_systems: ['Windows 7', 'Windows 10'],
      c2_servers: ['203.0.113.1'],
      capabilities: ['Banking Fraud', 'Data Exfiltration', 'Ransomware Delivery']
    }
  ];

  const getThreatLevelColor = (level) => {
    switch (level) {
      case 'Critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'High': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  return (
    <div className="h-full flex">
      {/* LEFT SIDEBAR */}
      <div className="w-[420px] border-r border-slate-800/50 bg-slate-900/30 flex flex-col">
        <div className="px-4 py-4 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <Bug className="w-5 h-5 text-cyan-400" />
              Malware Analysis
            </h2>
            {canEditIncidents && (
              <button className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-xs text-white font-medium transition-all">
                <Plus className="w-4 h-4 inline-block mr-1" />
                Add Sample
              </button>
            )}
          </div>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search malware samples..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
            />
          </div>

          <div className="mt-3 text-xs text-slate-400">
            {placeholderMalware.length} malware samples analyzed
          </div>
        </div>

        {/* MALWARE LIST */}
        <div className="flex-1 overflow-y-auto">
          {placeholderMalware.map((malware) => (
            <div
              key={malware.id}
              onClick={() => setSelectedMalware(malware)}
              className={`p-4 border-b border-slate-800/50 cursor-pointer transition-all ${
                selectedMalware?.id === malware.id
                  ? 'bg-cyan-500/10 border-l-4 border-l-cyan-500'
                  : 'hover:bg-slate-800/30'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-200 text-sm mb-1">{malware.name}</h3>
                  <span className="text-xs text-slate-400">{malware.family}</span>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium border ${getThreatLevelColor(malware.threat_level)}`}>
                  {malware.threat_level}
                </span>
              </div>
              
              <div className="flex items-center gap-4 text-xs text-slate-400 mt-2">
                <div className="flex items-center gap-1">
                  <FileCode className="w-3 h-3" />
                  {malware.type}
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(malware.last_seen).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedMalware ? (
          <>
            <div className="px-8 py-6 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-100 mb-2">{selectedMalware.name}</h2>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-slate-700/50 rounded-lg text-sm text-slate-300">
                      {selectedMalware.family}
                    </span>
                    <span className={`px-3 py-1 rounded-lg text-sm border ${getThreatLevelColor(selectedMalware.threat_level)}`}>
                      {selectedMalware.threat_level}
                    </span>
                  </div>
                </div>
                {canEditIncidents && (
                  <button className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-sm text-blue-400 transition-all">
                    Update Analysis
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
              <div className="max-w-4xl space-y-6">
                {/* File Information */}
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <FileCode className="w-5 h-5 text-cyan-400" />
                    File Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">File Type:</span>
                      <span className="ml-2 text-slate-200">{selectedMalware.file_type}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">File Size:</span>
                      <span className="ml-2 text-slate-200">{selectedMalware.file_size}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-500">File Hash:</span>
                      <div className="mt-1 p-2 bg-slate-900/50 rounded font-mono text-xs text-slate-300 break-all">
                        {selectedMalware.file_hash}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-cyan-400" />
                    Timeline
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">First Seen:</span>
                      <span className="ml-2 text-slate-200">
                        {new Date(selectedMalware.first_seen).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Last Seen:</span>
                      <span className="ml-2 text-slate-200">
                        {new Date(selectedMalware.last_seen).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Affected Systems */}
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-cyan-400" />
                    Affected Systems
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedMalware.affected_systems.map((system, idx) => (
                      <span key={idx} className="px-3 py-2 bg-blue-500/20 text-blue-300 rounded-lg text-sm border border-blue-500/30">
                        {system}
                      </span>
                    ))}
                  </div>
                </div>

                {/* C2 Servers */}
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Hash className="w-5 h-5 text-cyan-400" />
                    Command & Control Servers
                  </h3>
                  <div className="space-y-2">
                    {selectedMalware.c2_servers.map((server, idx) => (
                      <div key={idx} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 font-mono text-sm text-slate-200">
                        {server}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Capabilities */}
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-cyan-400" />
                    Capabilities
                  </h3>
                  <div className="space-y-2">
                    {selectedMalware.capabilities.map((capability, idx) => (
                      <div key={idx} className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <span className="text-sm text-red-300">{capability}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* MITRE ATT&CK Mapping */}
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4">
                    MITRE ATT&CK Mapping
                  </h3>
                  <div className="text-sm text-slate-400">
                    MITRE ATT&CK technique mappings would appear here
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            <div className="text-center">
              <Bug className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Select a malware sample to view analysis</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MalwareAnalysisView;